import React, { useState } from "react";
import { Loader2, Upload, FileUp, Link as LinkIcon, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProductIngestService } from "@/services/product-ingest.service";
import { ingestJobsCacheKey } from "@/services/ingest-jobs.service";
import { UploadService } from "@/services/upload.service";
import { AlertMessage } from "@/components/ui/alert-message";
import { cn } from "@/lib/utils";
import { useTasks } from "@/context/TasksContext";
import { useSWRConfig } from "swr";

interface ImportProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  catalogueId: string;
  catalogueName: string;
}

export function ImportProductsModal({
  isOpen,
  onClose,
  catalogueId,
  catalogueName,
}: ImportProductsModalProps) {
  const { addTask, updateTask } = useTasks();
  const { mutate } = useSWRConfig();
  const [importMethod, setImportMethod] = useState<"url" | "upload">("url");
  const [fileUrl, setFileUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [format, setFormat] = useState("csv");
  const [isLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);

  // URL validation function
  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      // Check if it's http or https protocol
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Auto-detect format from URL
  const detectFormatFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const extension = pathname.split('.').pop()?.toLowerCase();
      
      if (extension === 'csv' || extension === 'json' || extension === 'xml') {
        return extension;
      }
    } catch {
      // Invalid URL or no extension found
    }
    return format; // Keep current format if can't detect
  };

  // Handle URL change with auto-detection
  const handleUrlChange = (url: string) => {
    setFileUrl(url);
    if (url.trim()) {
      const detectedFormat = detectFormatFromUrl(url);
      if (detectedFormat !== format) {
        setFormat(detectedFormat);
      }
    }
  };

  // Simple toast notification
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 p-4 rounded-md text-white ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } shadow-lg z-50`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (importMethod === "url") {
      if (!fileUrl.trim()) {
        setError("Please enter a file URL");
        return;
      }
      if (!validateUrl(fileUrl)) {
        setError("Please enter a valid URL (e.g., https://example.com/products.csv)");
        return;
      }
    }
    if (importMethod === "upload" && !selectedFile) {
      setError("Please select a file to upload");
      return;
    }

    // Create task immediately
    const tempJobId = `upload-${Date.now()}`;
    const taskId = addTask({
      jobId: tempJobId,
      type: importMethod === "upload" ? "file-upload" : "url-import",
      status: "running",
      progress: 0,
      catalogueId,
      catalogueName,
    });

    // Close modal immediately
    handleClose();

    // Show toast notification
    showToast("Import started - check Tasks", "success");

    try {
      let finalUrl = fileUrl;

      // Handle file upload if that method is selected
      if (importMethod === "upload" && selectedFile) {
        try {
          finalUrl = await UploadService.uploadFile(selectedFile, (progress) => {
            updateTask(taskId, { progress });
          });
        } catch (uploadError: any) {
          updateTask(taskId, { 
            status: "errored", 
            error: `Upload failed: ${uploadError.message}` 
          });
          showToast(`Upload failed: ${uploadError.message}`, "error");
          return;
        }
      }

      // Determine format from file extension if upload
      let finalFormat = format;
      if (importMethod === "upload" && selectedFile) {
        if (selectedFile.name.endsWith(".json")) finalFormat = "json";
        else if (selectedFile.name.endsWith(".xml")) finalFormat = "xml";
        else finalFormat = "csv";
      }

      const result = await ProductIngestService.importProducts(
        catalogueId,
        finalUrl,
        finalFormat
      );

      // Ingestion runs asynchronously. Hand off the real jobId and keep the task
      // running — live progress and completion are reported by the progress
      // banner and Tasks page (which poll the job), not here on the 202.
      updateTask(taskId, {
        jobId: result.jobId,
        status: "running",
        progress: undefined,
      });

      // Surface the progress banner immediately.
      mutate(ingestJobsCacheKey(catalogueId));
    } catch (err: any) {
      updateTask(taskId, {
        status: "errored",
        error: err.message || "Failed to start import",
      });
      showToast(err.message || "Failed to start import", "error");
    }
  };

  const handleClose = () => {
    setFileUrl("");
    setSelectedFile(null);
    setFormat("csv");
    setError(null);
    setSuccessMessage(null);
    setUploadProgress("");
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.match(/\.(csv|json|xml)$/)) {
        setSelectedFile(file);
      } else {
        setError("Invalid file format. Please upload CSV, JSON, or XML.");
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Import Products</DialogTitle>
          <DialogDescription>
            Add products to <span className="font-semibold text-primary">{catalogueName}</span> via file upload or URL.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <AlertMessage message={error} type="error" />
          <AlertMessage message={successMessage} type="success" />
          
          {uploadProgress && (
            <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-md flex items-center gap-3 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {uploadProgress}
            </div>
          )}

          <Tabs value={importMethod} onValueChange={(v) => setImportMethod(v as "url" | "upload")} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="url" className="data-[state=active]:bg-pink-50 data-[state=active]:text-pink-700">
                <LinkIcon className="mr-2 h-4 w-4" /> Import from URL
              </TabsTrigger>
              <TabsTrigger value="upload" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700">
                <FileUp className="mr-2 h-4 w-4" /> Upload File
              </TabsTrigger>
            </TabsList>

             <TabsContent value="url" className="space-y-4">
               <div className="grid gap-4">
                 <div className="space-y-2">
                   <Label htmlFor="fileUrl">File URL</Label>
                   <div className="relative">
                     <LinkIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                     <Input
                       id="fileUrl"
                       value={fileUrl}
                       onChange={(e) => handleUrlChange(e.target.value)}
                       className="pl-9"
                       placeholder="https://example.com/products.csv"
                       required={importMethod === "url"}
                     />
                   </div>
                   <p className="text-xs text-muted-foreground">
                     Supported formats: CSV, JSON, XML (automatically detected from URL)
                   </p>
                 </div>
                

              </div>
            </TabsContent>

            <TabsContent value="upload" className="space-y-4">
              <div 
                className={cn(
                  "border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer",
                  isDragging ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300 hover:bg-gray-50",
                  selectedFile ? "bg-green-50 border-green-200" : ""
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-upload")?.click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".csv,.json,.xml"
                />
                
                {selectedFile ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-green-900">{selectedFile.name}</p>
                      <p className="text-xs text-green-700">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                    >
                      Remove file
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <Upload className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium text-gray-900">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Supported formats: CSV, JSON, XML (Max 10MB)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading || (importMethod === 'url' && !fileUrl) || (importMethod === 'upload' && !selectedFile)}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                  {uploadProgress ? "Processing..." : "Importing..."}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" /> Start Import
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
